import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { BrowserRouter as Router, Route, useHistory, useParams } from 'react-router-dom';
import { i18nIntl, isColorBright, isNotEmpty, RGB_2_HEX } from '../lib/ep';
import { useCSS } from '../lib/hooks';

import ActionButton from '../components/builder/ActionButton';
import ActionLink from '../components/builder/ActionLink';
import WinnerLayer from '../components/prizes/WinnerLayer';
import ButtonCollection from '../components/builder/ButtonCollection';

import LeveledHTMLText from '../components/front/helpers/LeveledHTMLText';
import RibbonCollection from '../components/ui_items/RibbonCollection';
import ClaimPromoFull, { rejectPrize } from './ClaimPromoFull';
import Layout from '../components/front/Layout';

const RedeemLink = ({ prize, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const is_redeemable = props?.is_redeemable ?? false;
	if(!is_redeemable) return null;

	const prize_type = prize?.PrizeType ?? false;
	const p = prize?.Prize ?? false;

	if(prize_type === false) return null;

	const display_status = props?.display_status ?? false;

	const redeemed = props?.redeemed ?? false;
	const expired = props?.expired ?? false;
	const redeem_link = `/redeem/${p.id}/${p.hash}`;

	if(display_status === false && (redeemed || expired)) return null;

	if(redeemed) {
		return (
			<strong class="in_top">{i18n("Redeemed")}</strong>
		)
	}

	if(expired) {
		return (
			<strong class="in_top">{i18n("***expired", 'ucfirst')}</strong>
		)
	}

	return (
		<ActionLink
			_href={redeem_link}
			_class={{other:'hb_mainFontWeightBold gui_capitalize_text'}}
		>***Redeem</ActionLink>
	)
};

const SinglePrize = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const history = useHistory();

	const is_rejecting = props?.is_rejecting ?? false;

	const _prize = props?.prize ?? false;
	if(_prize === false) return null;

	const prize = _prize.Prize ?? false;
	if(prize === false) return null;

	const prize_type = _prize.PrizeType ?? {};
	const promo_code = _prize.PromoCode ?? {};
	const claim = (is_rejecting) ? {} : ((_prize?.PromoClaim != undefined) ? _prize : (props?.claims[_prize.Prize.id] ?? {}));
	const code_validation = _prize?.CodeValidation ?? false;
	//const code_validation = _prize?.CodeValidation?.CodeValidation ?? (_prize?.CodeValidation ?? false);

	const prize_title = (isNotEmpty(prize_type, 'title')) ? prize_type.title : i18n("Unknown prize");

	if(props?.is_guest ?? false) {
		return (
			<li class="prize_item">
				<span class="my_prize_title">
					<strong class="gui_force_bold">{prize_title}</strong>
				</span>
			</li>
		)
	}

	const getDownloadableType = () => {
		return (isNotEmpty(prize_type, 'downloadable', ["", " ", "0"])) ? prize_type.downloadable : false;
	};

	const getDownloadLink = () => {
		if(getDownloadableType() === false) return false;
		if(prize.id == undefined) return `/prizes/prize_export/${prize_type.id}`; // in preview
		if(['coupon_maker', 'download'].includes(prize_type.downloadable_origin)) return `/prize_coupon/${prize.id}/${prize.hash}`;
		return false;
	};

	const isPrizeRedeemable = () => {
		if(isNotEmpty(prize_type, 'redeemable', ["", " ", "0"])) return true;
		return false;
	};

	const isPrizeClaimable = () => {
		if(isNotEmpty(prize_type, 'claimable', ["", " ", "0"])) return true;
		return false;
	};

	const is_redeemable = isPrizeRedeemable();
	const is_claimable = isPrizeClaimable();

	const isPrizeExpired = () => {
		if(!is_redeemable && !is_claimable) return false;

		if(is_redeemable && (prize?.seconds_to_redeem ?? 1) < 1) return true;
		if(is_claimable && (prize?.seconds_to_claim ?? 1) < 1) return true;
		return false;
	};

	const isPrizeRedeemed = () => {
		if(!is_redeemable) return false;

		if(code_validation !== false && code_validation.id) return true;
		return false;
	};

	const isPrizeClaimed = () => {
		if(!is_claimable) return false;

		if((claim?.PromoClaim?.status ?? '0') != '0') return true;
		return false;
	};

	const isPrizePendingToRedeem = () => {
		if(!is_redeemable) return false;

		if(expired === false && redeemed === false) return true;
		return false;
	};

	const isPrizePendingToClaim = () => {
		if(!is_claimable) return false;
		if (_prize.Prize?.claimed ?? false) return false;
		if((claim?.PromoClaim?.status ?? '0') == '0') return true;
		return false;
	};

	const download_link = getDownloadLink();

	const prize_image = (isNotEmpty(prize_type, 'full_image')) ? prize_type.full_image : false;
	const expired = isPrizeExpired();
	const redeemed = isPrizeRedeemed();
	const claimed = isPrizeClaimed();
	const pending_to_redeem = isPrizePendingToRedeem();
	const pending_to_claim = isPrizePendingToClaim();
	const classes = ['prize_item'];

	if(expired) classes.push('is_expired');
	if(redeemed || claimed) classes.push('is_redeemed');
	if(pending_to_redeem || pending_to_claim) classes.push('is_pending_to_redeem');
	
	const display_ribbon = (is_rejecting === false && (is_redeemable || is_claimable) && prize_image !== false) ? true : false;
	
	let ribbon_props;
	if(claimed) {
		ribbon_props = {
			title:i18n((is_claimable) ? "Claimed" : "Redeemed"),
			color:'--text-color-rgb',
			autotranslate:false,
			is_light:(isColorBright(RGB_2_HEX(props?.promo_text_color ?? 'rgba(0, 0, 0, 1)'))) ? true : false,
		};
	} else if(expired) {
		ribbon_props = {
			title:i18n("***expired", 'ucfirst'),
			color:'red',
			autotranslate:false,
		};
	} else if(pending_to_redeem || pending_to_claim) {
		ribbon_props = {
			title:i18n((is_claimable) ? "Claim it" : "Redeem it"),
			color:'green',
			autotranslate:false,
		};
	}

	const onClick = () => {
		if(claimed || expired) {
			history.push(prize.id);
			return;
		}
		props.openWinnerLayer(prize.id);
	};

	const from_fileset = (getDownloadableType() == '3') ? true : false;

	return (
		<li class={classes.join(" ")}>
			{(prize_image !== false && is_rejecting === false) && (
				<a onClick={onClick}>
					<img
						class="my_prize_image"
						src={prize_image}
						width="100%"
						alt=""
					/>
				</a>
			)}

			{(prize_image !== false && is_rejecting) && (
				<img
					class="my_prize_image"
					src={prize_image}
					width="100%"
					alt=""
				/>
			)}

			{(display_ribbon) && (
				<RibbonCollection
					items={[{ ...ribbon_props, onClick }]}
				/>
			)}

			<span class="my_prize_title">
				<strong class="gui_force_bold">{prize_title}</strong>

				{(claimed && isNotEmpty(promo_code, 'code') && from_fileset === false) && (
					<span> ({promo_code.code})</span>
				)}

				{(claimed && isNotEmpty(prize_type, 'unique_code') && from_fileset === false) && (
					<span> ({prize_type.unique_code})</span>
				)}
			</span>

			{(is_rejecting === false) && (
				<ButtonCollection _class={{ other:'my_prize_actions gui_padded_children_low' }}>
					<ActionLink
						onClick={onClick}
						_class={{ other:'hb_mainFontWeightBold gui_capitalize_text' }}
					>View</ActionLink>

					{(pending_to_claim && !isPrizeExpired()) && (
						<ActionLink
							onClick={() => history.push(props.prize.Prize.id)}
							_class={{ other:'hb_mainFontWeightBold gui_capitalize_text' }}
						>Claim</ActionLink>
					)}

					{(download_link !== false && !expired && !pending_to_claim) && (
						<ActionLink
							_href={download_link}
							_target="_blank"
							_class={{ other:'hb_mainFontWeightBold gui_capitalize_text' }}
						>Download</ActionLink>
					)}

					<RedeemLink
						prize={_prize}
						is_redeemable={is_redeemable}
						redeemed={redeemed}
						expired={expired}
						display_status={(prize_image !== false) ? false : true}
						intl={props.intl}
					/>
				</ButtonCollection>
			)}
		</li>
	)
};

const Wrapper = (props) => {
	const is_standalone = props?.is_standalone ?? false;
	const total_prizes = props?.total_prizes ?? 0;

	const winner_label = (total_prizes == 0) ? "You haven't won any prize yet." : ((total_prizes > 1) ? "You've won {prizes} prizes" : "You've won {prizes} prize");

	if(is_standalone) {
		return (
			<div class="prizes_won">
				<LeveledHTMLText>
					<h1>{props.intl.formatMessage({ id:winner_label }, { prizes:total_prizes })}</h1>
				</LeveledHTMLText>

				<ActionLink
					onClick={() => history.go(-1)}
					_class={{ other:'gui_not_in_top_small' }}
				>Go back</ActionLink>

				{props.children}
			</div>
		)
	}

	return (
		<div class="prizes_won">
			{props.children}
		</div>
	)
};

const ClaimPrize = (props) => {
	const { prize_id } = useParams();
	const history = useHistory();

	const prize_type =  props.prizes.find((p) => (p.Prize.id == prize_id));

	const rejectAndConfirmAction = () => {
		if(props.onReject != undefined) props.onReject(prize_type.Prize.id);

		rejectPrize(prize_type.Prize.promotion_id, prize_type.Prize.id, () => {
			history.push(`${prize_id}/reject/confirm/`);
		});
	};

	if(!prize_type) {
		window.location.href = `/my_prizes/${props.promotion.Promotion.id}`;
		return;
	}

	return (
		<ClaimPromoFull
			{...props}
			prize_id={prize_id}
			prize_type={prize_type.PrizeType}
			claim={props?.claims[prize_id]}
			onClaim={props.onClaim}
			goReject={() => history.push(`${prize_id}/reject/`)}
			goRejectConfirm={rejectAndConfirmAction}
			back={() => history.push('/')}
		/>
	)
};

const RejectConfirm = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	return (
		<LeveledHTMLText>
			<h1>{i18n("Confirmed!")}</h1>
			<p>{i18n("Your prize has been liberated.")}</p>
			<p>{i18n("Thank you.")}</p>
		</LeveledHTMLText>
	)
};

const RejectPrize = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const { prize_id } = useParams();
	const history = useHistory();

	const [ is_rejected, setIsRejected ] = useState(false);
	const prize_type =  props.prizes.find((p) => (p.Prize.id == prize_id));

	if(!prize_type || prize_type?.PromoClaim != undefined) {
		//window.location.href = `/my_prizes/${props.promotion.Promotion.id}`;
		history.replace('/');
		return null;
	}

	const rejectAction = () => {
		if(props.onReject != undefined) props.onReject(prize_type.Prize.id);

		rejectPrize(prize_type.Prize.promotion_id, prize_type.Prize.id, () => {
			setIsRejected(true);
			//history.push('/');
		});
	};

	if(is_rejected) {
		return (
			<RejectConfirm
				intl={props.intl}
			/>
		)
	}

	return (
		<div>
			<ul class="say_bye_to_prize">
				<SinglePrize
					prize={prize_type}
					promo_text_color={props?.promotion?.Promotion?.layout_props?.color?.text_color}
					is_rejecting={true}
					intl={props.intl}
				/>
			</ul>

			<LeveledHTMLText>
				<p>{i18n("If you don't want to enjoy this prize, click the button below to release it and let someone else enjoy it.")}</p>
			</LeveledHTMLText>

			<div class="gui_flexbox gui_flex_col gui_not_in_top" style={{ gap:'0.5em' }}>
				<ButtonCollection
					_class={{ other:'flex_button' }}
				>
					<ActionButton
						_fullclass=""
						onClick={rejectAction}
					>I don't want the prize</ActionButton>
				</ButtonCollection>

				<ActionLink
					onClick={() => history.push(`/${prize_id}`)}
				>Yes, I want the prize</ActionLink>
			</div>
		</div>
	)
};

const UserPrizes = (props) => {
	useCSS('comp/my_prizes.css');

	const [ current, setCurrent ] = useState(0);
	const [ prizes, setPrizes ] = useState(props?.prizes ?? []);
	const [ display_prize_layer, setDisplayPrizeLayer ] = useState(false);
	
	const is_guest = props?.is_guest ?? false;
	const total_prizes = prizes.length;

	const openWinnerLayer = (prize_id) => {
		let selected_prize = 0;
		for(let i in prizes) {
			if(prizes[i].Prize.id == prize_id) {
				selected_prize = i;
				break;
			}
		}

		setCurrent(selected_prize);
		setDisplayPrizeLayer(true);
	};

	if(total_prizes == 0) {
		return (
			<Wrapper
				is_standalone={props.is_standalone}
				intl={props.intl}
			/>
		);
	}

	const onClaim = (prize_id, data) => {
		setPrizes((prizes) => {
			return prizes.map((prize) => {
				if(prize.Prize.id != prize_id) return prize;

				prize.Prize.claimed = true;
				if(data) prize.PromoClaim = data;

				return prize;
			});
		})
	}

	const onReject = (prize_id) => {
		setPrizes((prizes) => {
			return prizes.filter((prize) => (prize.Prize.id != prize_id));
		});
	};

	const section_main_path = `/my_prizes/${props.promotion.Promotion.id}`;

	return (
		<Router basename={section_main_path} >
			<Route exact={true} path={`/`}>
				<Layout
					page='my_prizes'
					langs={props.common.langs}
					{...props}
				>
					<Wrapper
						is_standalone={props.is_standalone}
						total_prizes={total_prizes}
						intl={props.intl}
					>
						<ul class="won_prizes_textual_list in_boxes">
							{prizes.map((prize, k) => {
								return (
									<SinglePrize
										key={`prize_${k}`}
										prize={prize}
										openWinnerLayer={openWinnerLayer}
										is_guest={is_guest}
										claims={props.claims}
										onReject={onReject}
										promo_text_color={props?.promotion?.Promotion?.layout_props?.color?.text_color}
										intl={props.intl}
									/>
								)
							})}
						</ul>

						{(display_prize_layer && !is_guest) && (
							<WinnerLayer
								prize={prizes[current].PrizeType}
								user_prize={prizes[current].Prize}
								user={prizes[current].User}
								share={prizes[current].Share}
								code={prizes[current].PromoCode}
								code_validation={prizes[current]?.CodeValidation ?? null}
								autoplay={true}
								backButton={() => setDisplayPrizeLayer(false)}
							/>
						)}
					</Wrapper>
				</Layout>
			</Route>

			<Route exact={true} path={`/:prize_id`}>
				<Layout
					page='claim'
					langs={props.common.langs}
					{...props}
				>
					<ClaimPrize
						promotion={props.promotion}
						locale={props.locale}
						prizes={prizes}
						user_prizes={prizes}
						claims={props.claims}
						user={props.user}
						onClaim={onClaim}
						onReject={onReject}
					/>
				</Layout>
			</Route>

			<Route exact={true} path={`/:prize_id/reject`}>
				<Layout
					page='reject'
					langs={props.common.langs}
					{...props}
				>
					<RejectPrize
						promotion={props.promotion}
						locale={props.locale}
						prizes={props.prizes}
						user_prizes={prizes}
						claims={props.claims}
						user={props.user}
						onClaim={onClaim}
						onReject={onReject}
						intl={props.intl}
					/>
				</Layout>
			</Route>

			<Route exact={true} path={`/:prize_id/reject/confirm`}>
				<Layout
					page='reject'
					langs={props.common.langs}
					{...props}
				>
					<RejectConfirm
						intl={props.intl}
					/>
				</Layout>
			</Route>
		</Router>
	)
};

export default injectIntl(UserPrizes);